import {Injectable} from '@angular/core';
import {SocketMessagesService} from '../core/socket/socket.service';
import {Observable, of, Subject} from 'rxjs';
import {ICaptionSource} from '../types';
import {distinctUntilChanged, filter, map, pluck, shareReplay, switchMap, tap} from 'rxjs/operators';
import {AuthService} from '../core/auth.service';

interface IExternalCaptionSource extends ICaptionSource {
    organisations: number[];
}

@Injectable({
    providedIn: 'root'
})
export class DisplayCaptionsService {
    private captions$$: Subject<ICaptionSource[]> = new Subject<ICaptionSource[]>();
    private reloadCaptions$$: Subject<void> = new Subject<void>();
    private captionsCacheMap: Map<number, ICaptionSource[]> = new Map();
    public captions$: Observable<ICaptionSource[]> = this.captions$$.asObservable().pipe(
        shareReplay(1)
    );

    constructor(
        private messages: SocketMessagesService,
        private auth: AuthService,
    ) {
        // Do not clear captionsCacheMap before the request completes: shareReplay would still replay
        // the previous emission while the map is empty, and org-scoped reads would return [] until
        // the next fetch — leaving display-caption dropdowns empty. Clear inside getCaptions$ when data arrives.
        this.reloadCaptions$$.asObservable().pipe(
            switchMap(() => this.getCaptions$()),
            tap(this.captions$$.next.bind(this.captions$$))
        ).subscribe();

        this.auth.isLoggedIn$.pipe(
            distinctUntilChanged(),
            filter((loggedIn) => loggedIn === true),
            tap(() => this.reloadCaptions())
        ).subscribe();
    }

    public reloadCaptions(): void {
        this.reloadCaptions$$.next();
    }

    public getCaptionsMap$(): Observable<Map<number, ICaptionSource>> {
        return this.captions$.pipe(
            map(captions => {
                return new Map(captions.map(caption => {
                    return [caption.caption_output_id, caption];
                }));
            })
        );
    }

    public getCaptionsByOrg$(id: number): Observable<ICaptionSource[]> {
        return (this.captionsCacheMap.has(id)) ? of(this.captionsCacheMap.get(id) as ICaptionSource[]) : of([]);
    }

    /** Synchronous read after `captions$` has emitted (cache populated). Used to avoid racing `getCaptionsByOrg$` before load completes. */
    public getCachedCaptionsForOrg(orgId: number): ICaptionSource[] {
        return this.captionsCacheMap.has(orgId)
            ? ([...(this.captionsCacheMap.get(orgId) as ICaptionSource[])])
            : [];
    }

    private getCaptions$(): Observable<ICaptionSource[]> {
        // @ts-ignore
        return this.messages.request$<{ caption_outputs: IExternalCaptionSource[] }>('getCaptionOutputs').pipe(
            pluck('caption_outputs'),
            tap((captions: any) => {
                this.captionsCacheMap.clear();
                captions.forEach((_caption: any) => {
                    const {caption_output_id, caption_params, caption_output_name, organisations} = _caption;
                    const caption = {caption_output_id, caption_params, caption_output_name};
                    organisations.forEach((org_id: any) => {
                        if (this.captionsCacheMap.has(org_id)) {
                            const organizationCaptions = this.captionsCacheMap.get(org_id) as ICaptionSource[];
                            organizationCaptions.push(caption);
                            this.captionsCacheMap.set(org_id, organizationCaptions);
                        } else {
                            this.captionsCacheMap.set(org_id, [caption]);
                        }
                    });
                });
            })
        );
    }
}
